import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  User,
  Building2,
  Mail,
  Shield,
  LogOut,
  Key,
  Link2,
  Check,
  Loader2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import TelegramConnectionCard from '@/components/TelegramConnectionCard';

// Icône Google SVG
const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export const SettingsPage = () => {
  const { user, logout, token } = useAuth();
  const { currentTenant } = useWorkspace();
  const navigate = useNavigate();
  const [googleLinked, setGoogleLinked] = useState(false);
  const [googleEmail, setGoogleEmail] = useState('');
  const [linkingGoogle, setLinkingGoogle] = useState(false);
  const [unlinkingGoogle, setUnlinkingGoogle] = useState(false);

  // Vérifier si le compte Google est lié au chargement
  useEffect(() => {
    const checkGoogleLink = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/auth/google/link-status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setGoogleLinked(data.linked);
          setGoogleEmail(data.google_email || '');
        }
      } catch (error) {
        console.error('Erreur vérification lien Google:', error);
      }
    };
    
    if (token) {
      checkGoogleLink();
    }
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  /**
   * Lier le compte Google
   * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
   */
  const handleLinkGoogle = () => {
    setLinkingGoogle(true);
    // Stocker le token pour le récupérer après le callback
    sessionStorage.setItem('link_google_token', token);
    // Rediriger vers Emergent Auth avec un paramètre spécial pour le linking
    const redirectUrl = window.location.origin + '/parametres?link_google=true';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  /**
   * Délier le compte Google
   */
  const handleUnlinkGoogle = async () => {
    setUnlinkingGoogle(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/auth/google/unlink`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        setGoogleLinked(false);
        setGoogleEmail('');
        toast.success('Compte Google délié avec succès');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur lors de la déliage');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setUnlinkingGoogle(false);
    }
  };

  // Gérer le callback de liaison Google
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const linkGoogle = urlParams.get('link_google');
    const hash = window.location.hash;
    
    if (linkGoogle === 'true' && hash.includes('session_id=')) {
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        const savedToken = sessionStorage.getItem('link_google_token');
        
        if (savedToken) {
          // Lier le compte Google
          const linkAccount = async () => {
            try {
              const API_URL = process.env.REACT_APP_BACKEND_URL;
              const response = await fetch(`${API_URL}/api/auth/google/link`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${savedToken}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ session_id: sessionId }),
              });
              
              if (response.ok) {
                const data = await response.json();
                setGoogleLinked(true);
                setGoogleEmail(data.google_email || '');
                toast.success('Compte Google lié avec succès !');
              } else {
                const data = await response.json();
                toast.error(data.detail || 'Erreur lors de la liaison');
              }
            } catch (error) {
              toast.error('Erreur de connexion');
            } finally {
              sessionStorage.removeItem('link_google_token');
              // Nettoyer l'URL
              window.history.replaceState({}, document.title, '/settings');
            }
          };
          
          linkAccount();
        }
      }
    }
  }, []);

  const roleLabels = {
    dirigeant: 'Dirigeant',
    salarie: 'Salarié',
    comptable: 'Comptable',
    prestataire: 'Prestataire',
    owner: 'Propriétaire',
    admin: 'Administrateur',
    user: 'Utilisateur',
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Paramètres</h1>
        <p className="text-muted-foreground mt-1">Gérez votre compte et vos préférences</p>
      </div>

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Profil
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-semibold">
                {user?.prenom} {user?.nom}
              </h3>
              <Badge variant="outline" className="mt-1 capitalize">
                {roleLabels[user?.role] || user?.role}
              </Badge>
            </div>
          </div>

          <Separator />

          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                id="email"
                value={user?.email || ''}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Rôle
              </Label>
              <Input
                value={roleLabels[user?.role] || user?.role || ''}
                disabled
                className="bg-muted capitalize"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Exploitation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Exploitation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="p-4 rounded-lg bg-muted/50 text-center">
            <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              {currentTenant?.nom || 'Frédéric Développement'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              ID: {currentTenant?.id || user?.exploitation_id || 'Non défini'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Sécurité
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button variant="outline" className="w-full" disabled>
            Changer le mot de passe
          </Button>
          
          <Separator />
          
          <Button 
            variant="destructive" 
            className="w-full"
            onClick={handleLogout}
            data-testid="settings-logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Se déconnecter
          </Button>
        </CardContent>
      </Card>

      {/* Compte Google */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Compte Google
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {googleLinked ? (
            <>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                  <GoogleIcon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-green-700 dark:text-green-400">Compte lié</span>
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <p className="text-sm text-muted-foreground">{googleEmail}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Vous pouvez vous connecter avec Google ou avec votre email/mot de passe.
              </p>
              <Button 
                variant="outline" 
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleUnlinkGoogle}
                disabled={unlinkingGoogle}
                data-testid="unlink-google-button"
              >
                {unlinkingGoogle ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Link2 className="w-4 h-4 mr-2" />
                )}
                Délier le compte Google
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Liez votre compte Google pour vous connecter plus rapidement et sécuriser votre compte.
              </p>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleLinkGoogle}
                disabled={linkingGoogle}
                data-testid="link-google-button"
              >
                {linkingGoogle ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <GoogleIcon className="w-5 h-5 mr-2" />
                )}
                Lier mon compte Google
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <TelegramConnectionCard />
    </div>
  );
};

export default SettingsPage;
